/**
 * Identifiers. Every row id is a UUIDv7 generated in the app (CLAUDE.md, coding
 * conventions): time-ordered, so B-tree inserts stay local, and safe to mint
 * offline on a till without asking the server.
 */
import { v7 as uuidv7 } from 'uuid';
import { z } from 'zod';
import type { Brand } from './brand.js';

/** A lowercase, canonical (8-4-4-4-12) RFC 9562/4122 UUID string. */
export type Uuid = Brand<string, 'Uuid'>;

/**
 * Canonical lowercase form, version nibble 1..8, RFC 4122 variant (10xx).
 * The nil and max UUIDs are rejected: they are never valid row ids.
 */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const UUID_V7_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export function isUuid(value: unknown): value is Uuid {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

export function isUuidV7(value: unknown): value is Uuid {
  return typeof value === 'string' && UUID_V7_PATTERN.test(value);
}

/**
 * Any RFC 4122 UUID in lowercase canonical form. Ids that arrive from legacy
 * or partner systems may be other versions, so this does not insist on v7.
 * Uppercase input is rejected, not normalised: ids are compared as strings.
 *
 * Brands with `.brand()` (type-only) and checks with `.regex()`, so the
 * generated JSON Schema / OpenAPI carries the `pattern`.
 */
export const uuidSchema = z
  .string()
  .regex(UUID_PATTERN, { message: 'Expected a lowercase canonical UUID' })
  .brand<'Uuid'>();

/** A UUIDv7, for ids this system mints itself. */
export const uuidV7Schema = z
  .string()
  .regex(UUID_V7_PATTERN, { message: 'Expected a lowercase canonical UUIDv7' })
  .brand<'Uuid'>();

/** Mint a new UUIDv7. Monotonic within a process, even inside one millisecond. */
export function newId(): Uuid {
  return uuidv7() as Uuid;
}

/** Milliseconds since the epoch encoded in a UUIDv7's first 48 bits. */
export function uuidV7Timestamp(id: Uuid): number {
  if (!isUuidV7(id)) throw new TypeError(`Not a UUIDv7: ${id}`);
  return Number.parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}
